<template>
  <div
    id="root"
    ref="rootEl"
    v-hotkey="shortcuts"
    :class="String($route.name ?? '')"
    :style="{ '--menu-width': `${menuWidth}px` }"
  >
    <ContextMenu ref="contextMenu" />
    <ModalsContainer />
    <FeedbackModal v-if="useFeedback?.isUploading?.value" />
    <SimpleModal ref="simpleModal" />
    <RepeatModal ref="repeatModal" />
    <MultipleEventsModal ref="multipleEventsModal" />
    <DatePicker ref="datePicker" />
    <RecurrencePicker ref="recurrencePicker" />
    <DestinationPicker ref="destinationPicker" />
    <AssigneePicker ref="assigneePicker" />
    <StatusPicker ref="statusPicker" />
    <ModalConsole ref="console" />
    <ModalSearch ref="searchModal" />
    <ModalPalette ref="paletteModal" />
    <AccountIssuesModal
      v-if="showIssuesModal && accountIssues.length > 0"
      :issues="accountIssues"
      @close="closeIssuesModal"
    />
    <div
      id="titlebar"
      :class="[{ isMenuExpanded }, { isEditing }]"
      @dblclick="handleDoubleClick()"
    >
      <!-- Teleport Receiver -->
      <div id="dragbar" />
    </div>
    <Notifications v-if="isNotificationShown" :hook="notificationsHook" />
    <AutomationWidget />
    <NotificationNewAppVersion
      v-if="newVersion && hasCompletedOnboarding"
      :new-app-version="newVersion"
    />
    <Menu
      v-if="hasCompletedOnboarding"
      ref="menuEl"
      @toggle-menu-expand="toggleMenuExpand"
      @search="search"
    />
    <main
      v-if="hasCompletedOnboarding"
      id="content"
      :class="[String($route.name ?? ''), { isMenuExpanded }]"
    >
      <RouterView v-slot="{ Component, route }">
        <Suspense @resolve="onRouteResolved">
          <component
            :is="Component"
            :key="routeComponentKey(route)"
            @open-task="openTask"
          />
        </Suspense>
      </RouterView>
    </main>
    <Onboarding
      v-else
      :method="onboardingMethod"
      @onboarding-completed="onOnboardingCompleted"
    />
  </div>
</template>

<script lang="ts">
import { syncRefs } from "@vueuse/core";
import * as _ from "lodash-es";
import { match } from "ts-pattern";
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  provide,
  Ref,
  ref,
  shallowRef,
  toRefs,
  watch,
  watchEffect,
} from "vue";
import { ModalsContainer } from "vue-final-modal";
import { useRoute } from "vue-router";

import controller from "@ctrl/controller";
import type { Locale } from "@ctrl/controller";
import type {
  AllMenuItems,
  Controller,
  GraphDatabaseDictionary,
  GraphDatabaseDictionaryDictionary,
  Notification as NotificationSchema,
  ReleasableRef,
  Task,
  Time,
} from "@ctrl/controller-rpc";
import type { Hook } from "@ctrl/hook";

import { useAgents, type UseAgents } from "@/components/ai/useAgents";
import {
  useAutomationHotkeys,
  type UseAutomationHotkeys,
} from "@/components/ai/useAutomationHotkeys";
import {
  useAutomations,
  type UseAutomations,
} from "@/components/ai/useAutomations";
import BaseViewLoader from "@/components/base/BaseViewLoader.vue";
import { useCanvases, type UseCanvases } from "@/components/canvas/useCanvases";
import { toggleConsole } from "@/components/console";
import ContextMenu from "@/components/context-menu/ContextMenu.vue";
import { isUserDefinedDatabase } from "@/components/databases";
import { inboxBadgeKey, inboxFilterKey } from "@/components/inbox/inbox";
import { useIntegrations } from "@/components/integrations/useIntegrations";
import { goToKBShortcuts } from "@/components/menu/controller";
import Menu from "@/components/menu/Menu.vue";
import { launchFatalModal } from "@/components/modals/fatal";
import FeedbackModal from "@/components/modals/FeedbackModal.vue";
import MultipleEventsModal from "@/components/MultipleEventsModal.vue";
import { useNativeNotification } from "@/components/notification/nativeNotification";
import NotificationNewAppVersion from "@/components/notification/NotificationNewAppVersion.vue";
import RepeatModal from "@/components/RepeatModal.vue";
import { useIntegrationTaskBindingRepair } from "@/components/settings/integrations/useIntegrationTaskBindingRepair";
import {
  assignShortcuts,
  closeFeedback as closeFeedbackShortcut,
  goToMenuItem1,
  goToMenuItem2,
  goToMenuItem3,
  goToMenuItem4,
  goToMenuItem5,
  goToMenuItem6,
  goToMenuItem7,
  goToMenuItem8,
  goToMenuItem9,
  goToNextMenuItem,
  goToNextScreen,
  goToPreviousMenuItem,
  goToPreviousScreen,
  goToSettings,
  goToShortcuts,
  newItem,
  saveState,
  Shortcuts,
  testShortcuts,
  toggleDarkMode as toggleDarkModeShortcut,
  toggleMenu,
  toggleModalConsole,
  toggleSearch,
} from "@/components/shortcuts";
import SimpleModal from "@/components/SimpleModal.vue";
import { defaultTableIds } from "@/components/tables";
import {
  defaultTaskParentFilter,
  TaskParentFilter,
} from "@/components/task/types";
import { provideFlushPendingChanges } from "@/composables/flushPendingChanges";
import { useBrowserStore } from "@/composables/useBrowserStore";
import {
  defaultCalendarIssue,
  useCalendars,
  type UseCalendars,
} from "@/composables/useCalendars";
import { useMenu } from "@/composables/useMenu";
import { setLastAccessedOrganization } from "@/composables/useOrganization";
import { useWideDocument } from "@/composables/useWideDocument";
import { UseFeedback } from "@/hooks/support/feedback";
import { repairPagesHierarchy } from "@/page";
import { setDefaultMeetingAutomation } from "@/recording/automation";
import { useReleaser } from "@/ref";
import { routeComponentKey } from "@/routes/componentKey";
import { RouteNames } from "@/routes/types";
import { browser } from "@/services/browser";
import { useBilling } from "@/utils/billing";
import type { Billing } from "@/utils/billing";
import { in_electron, in_mac } from "@/utils/in";
import {
  displayedAgendaDate,
  navigateBackward,
  navigateForward,
  navigateTo,
  navigateToObject,
} from "@/utils/navigate";
import { openExternalUrl } from "@/utils/open-external-url";

import AutomationWidget from "./ai/AutomationWidget.vue";
import AssigneePicker from "./AssigneePicker.vue";
import DatePicker from "./DatePicker.vue";
import DestinationPicker from "./DestinationPicker.vue";
import * as inject from "./inject";
import ModalConsole from "./ModalConsole.vue";
import ModalPalette from "./ModalPalette.vue";
import AccountIssuesModal, {
  type AccountIssue,
} from "./modals/AccountIssuesModal.vue";
import ModalSearch from "./ModalSearch.vue";
import Notifications from "./notification/Notifications.vue";
import Onboarding, { type OnboardingMethod } from "./Onboarding.vue";
import {
  buildPostSetupOperations,
  POST_ONBOARDING_QUEUED_OPS_KEY,
  runPostSetupQueue,
  type PostSetupOperation,
} from "./onboarding/postSetupQueue";
import { createPalette } from "./palette/palette";
import RecurrencePicker from "./RecurrencePicker.vue";
import { UseSettings } from "./settings/controller/settings";
import { isBetaTesterIdentity } from "./settings/identity";
import StatusPicker from "./StatusPicker.vue";

const { locale, version } = controller;

export default defineComponent({
  components: {
    ModalsContainer,
    BaseViewLoader,
    ContextMenu,
    Menu,
    ModalConsole,
    ModalPalette,
    ModalSearch,
    FeedbackModal,
    MultipleEventsModal,
    NotificationNewAppVersion,
    Notifications,
    AutomationWidget,
    Onboarding,
    RepeatModal,
    AccountIssuesModal,
    SimpleModal,
    AssigneePicker,
    DatePicker,
    DestinationPicker,
    RecurrencePicker,
    StatusPicker,
  },
  props: {
    controller: { type: Object as PropType<Controller>, required: true },
    notificationsHook: {
      type: Object as PropType<Hook<NotificationSchema>>,
      required: true,
    },
    hasCompletedOnboardingPreviously: { type: Boolean, required: true },
  },
  async setup(props) {
    const theme = inject.theme();
    const themePreference = inject.themePreference();
    const route = useRoute();
    const router = inject.router();
    const logger = inject.logger("Routine");
    const baseLogger = logger.base();
    const isModalShown = inject.isModalShown();
    const isInputFocused = inject.isInputFocused();
    const newVersion = inject.newVersion();
    const profile = inject.profile();
    const logout = inject.logout();
    const showIssuesModal = ref(true);
    const closeIssuesModal = () => {
      showIssuesModal.value = false;
    };
    inject.disableObjectCreation(ref(false));
    const { hasCompletedOnboardingPreviously } = toRefs(props);
    const { controller } = props;
    const { notificationsHook } = toRefs(props);
    inject.controller(controller);
    const issuesRef = ref<Awaited<ReturnType<typeof controller.issues>> | null>(
      null
    );
    const isBetaTester = computed(
      () =>
        identities.value.find((p) => isBetaTesterIdentity(p.email)) !==
        undefined
    );
    inject.isBetaTester(isBetaTester);

    const releaser = useReleaser();

    const menuItemsRef = shallowRef<ReleasableRef<AllMenuItems> | null>(null);
    const menuItemsPromise = releaser.add(
      controller.menuItems.list().then((ref) => {
        menuItemsRef.value = ref;
        return ref;
      })
    );

    const menu = useMenu(
      controller,
      router,
      profile,
      baseLogger,
      route,
      menuItemsRef
    );
    releaser.defer(menu.release);
    inject.menu(menu);
    const {
      navigateToQuickAccessMenuItem,
      findPreviousQuickAccessMenuItemIndex,
      findNextQuickAccessMenuItemIndex,
    } = menu;
    // Initial reconciliation can safely remove stale database pins because both
    // snapshots are loaded. Runtime deletion handling stays in MenuItem.vue to
    // avoid racing menu updates against database-list updates.
    const cleanupInitialStaleDatabasePins = async (
      menuItems: AllMenuItems,
      databases: GraphDatabaseDictionaryDictionary
    ) => {
      const staleDatabasePins = menuItems.visible.filter((item) => {
        if (item.target.kind !== "database") return false;
        return !databases[item.target.workspace_id]?.[item.target.table_id];
      });

      await Promise.all(
        staleDatabasePins.map((item) => menu.unpinDatabase(item))
      );
    };
    const menuEl = ref<InstanceType<typeof Menu>>();

    // Set up the browser store to namespace keys with the profile ID
    const { syncState } = useBrowserStore(profile.value.user.accountId);

    const localeProvided = ref<Locale>();
    provide("locale", localeProvided);
    const inboxFilter = releaser.add(
      syncState<TaskParentFilter>(inboxFilterKey, defaultTaskParentFilter)
    );
    // Filters persisted before a parent kind existed (e.g. `object`) lack
    // its key: fill it in from the defaults so the kind is shown, not hidden.
    inboxFilter.value = { ...defaultTaskParentFilter, ...inboxFilter.value };
    inject.inboxFilter(inboxFilter);
    const inboxBadge = releaser.add(syncState<boolean>(inboxBadgeKey, true));
    inject.inboxBadge(inboxBadge);
    const changeMode = async () =>
      await controller.foreground(!document.hidden);
    onMounted(() => document.addEventListener("visibilitychange", changeMode));
    releaser.defer(() =>
      document.removeEventListener("visibilitychange", changeMode)
    );
    // Fatal errors
    const fatalError = ref<string | null>(null);
    {
      const hook = notificationsHook.value;
      const subscription = hook.attach((notif) => {
        const message = notif.message.map((o) => o.toString()).join("");
        if (notif.type === "error") {
          window.console.error(message);
          fatalError.value = message;
          openFatalModal();
        } else if (notif.type === "warning") window.console.warn(message);
        else window.console.log(message);
      });
      releaser.defer(() => hook.detach(subscription));
    }
    if (in_electron) {
      const { ipcRenderer: ipc } = require("electron");
      const onControllerCrashed = (
        _event: unknown,
        { code, signal }: { code: number | null; signal: string | null }
      ) => {
        const message = `Controller exited unexpectedly (code=${code}, signal=${signal})`;
        window.console.error(message);
        fatalError.value = message;
        openFatalModal();
      };
      ipc.on("controller-crashed", onControllerCrashed);
      releaser.defer(() =>
        ipc.removeListener("controller-crashed", onControllerCrashed)
      );
    }

    const console = ref<InstanceType<typeof ModalConsole>>();
    provide("console", console);
    provide("consoleModal", console);
    const hasCompletedOnboarding = ref(hasCompletedOnboardingPreviously.value);
    const isNotificationShown = computed(() => hasCompletedOnboarding.value);
    const isMenuExpanded = menu.isExpanded;
    const menuWidth = menu.width;
    if (hasCompletedOnboarding.value) {
      controller.flag
        .get("isMenuExpanded")
        .then((v: boolean | null) => (isMenuExpanded.value = !!v));
    }
    const isContextualCaptureEnabled = ref<boolean>(false);
    if (in_electron && in_mac()) {
      // Seed from the persisted flag without echoing it back over IPC: the
      // main process reads the flag itself at startup, and with several
      // windows each renderer would otherwise re-send (and re-persist) the
      // same value at boot. Only genuine toggles are forwarded.
      let mainProcessValue: boolean | undefined;
      controller.flag.get("captureContext").then((v: boolean | null) => {
        // A toggle that raced the seed wins; don't clobber it.
        if (mainProcessValue !== undefined) return;
        mainProcessValue = !!v;
        isContextualCaptureEnabled.value = !!v;
      });
      watch(isContextualCaptureEnabled, (enabled) => {
        if (enabled === mainProcessValue) return;
        mainProcessValue = enabled;
        const { ipcRenderer: ipc } = require("electron");
        ipc.send("change-capture-context", enabled);
      });
    }
    inject.isContextualCaptureEnabled(isContextualCaptureEnabled);

    const isMcpServerEnabled = ref<boolean>(false);
    controller.flag
      .get("isMcpServerEnabled")
      .then((v: boolean | null) => (isMcpServerEnabled.value = !!v));
    inject.isMcpServerEnabled(isMcpServerEnabled);
    const contextMenu = ref(null);
    provide("contextMenu", contextMenu);
    const datePicker = ref(null);
    provide("datePicker", datePicker);
    const recurrencePicker = ref(null);
    provide("recurrencePicker", recurrencePicker);
    const destinationPicker = ref(null);
    provide("destinationPicker", destinationPicker);
    const assigneePicker = ref(null);
    provide("assigneePicker", assigneePicker);
    const statusPicker = ref(null);
    provide("statusPicker", statusPicker);
    const simpleModal = ref(null);
    provide("simpleModal", simpleModal);
    const repeatModal = ref(null);
    provide("repeatModal", repeatModal);
    const multipleEventsModal = ref(null);
    provide("multipleEventsModal", multipleEventsModal);
    const searchModal = ref<InstanceType<typeof ModalSearch>>();
    const paletteModal = ref<InstanceType<typeof ModalPalette>>();
    const content: Ref<InstanceType<typeof HTMLElement> | null> = ref(null);
    inject.router(router);
    const clockProvided = ref<Time>();
    watch(
      () => clockProvided.value?.getDate(),
      (_) => (displayedAgendaDate.value = new Date())
    );
    provide("clock", clockProvided);
    // The app-wide calendar store: loaded in the awaited block below,
    // components resolve event calendars from it instead of per-event RPCs.
    const calendarsProvided = ref<UseCalendars>();
    inject.calendars(calendarsProvided);
    // The controller's live "today" (flips at midnight), shared app-wide
    // instead of one `controller.today()` subscription per consumer. The
    // placeholder is never observed: the subscription lands before this
    // component (and thus any consumer) renders.
    const todayProvided = ref<Time>(new Date());
    inject.today(todayProvided);
    const defaultTypesProvided = ref<GraphDatabaseDictionary>();
    inject.personalDefaultTypes(defaultTypesProvided);
    const customTypesProvided = ref<GraphDatabaseDictionary>();
    inject.personalCustomTypes(customTypesProvided);
    const typesProvided = ref<GraphDatabaseDictionary>();
    inject.types(typesProvided);
    const workspaceDatabasesProvided = ref<GraphDatabaseDictionaryDictionary>();
    inject.workspaceDatabases(workspaceDatabasesProvided);

    // The route is the source of truth for the organization being viewed:
    // whenever a route carries an orgId param for an organization the user
    // belongs to, persist it as the last accessed organization for org-less
    // screens (e.g. the settings pages).
    watch(
      () => route.params.orgId,
      (orgId) => {
        if (
          typeof orgId === "string" &&
          profile.value.organizations.some((o) => o.id === orgId)
        ) {
          setLastAccessedOrganization(profile.value, orgId);
        }
      },
      { immediate: true }
    );

    // The app-wide Membrane store: loads every organization's
    // services/connections once in the background (so Assistant `>`
    // suggestions are instant when an editor opens later) and shares them
    // with every consumer. Only the integration settings mutations trigger
    // refreshes after this.
    const integrations = useIntegrations(controller, baseLogger, profile);
    releaser.defer(integrations.release);
    inject.integrations(integrations);

    const billing = shallowRef<Billing | undefined>(undefined);
    inject.billing(billing);
    const billingP = releaser.add(useBilling(controller, baseLogger, profile));
    billingP.then((b) => {
      billing.value = b;
      // Keep plugin state synced with entitlement values as billing data settles.
      if (in_electron) {
        const { ipcRenderer: ipc } = require("electron");
        const menuBarEntitlement = b.isEntitledTo("menu-bar-widget");
        // Do not send anything unless the entitlement is genuinely revoked
        // (true -> false). The entitlement transiently starts as false during
        // billing hydration before settling; acting on that would erase the
        // user's stored preference on every startup.
        releaser.defer(
          watch(
            menuBarEntitlement,
            (isMenuBarEntitled, oldValue) => {
              if (isMenuBarEntitled === false && oldValue === true) {
                ipc.send("toggle-menubar", false);
              }
            },
            { immediate: true }
          )
        );

        if (in_mac()) {
          const contextualCaptureEntitlement =
            b.isEntitledTo("contextual-capture");
          releaser.defer(
            watch(
              contextualCaptureEntitlement,
              (isContextualCaptureEntitled, oldValue) => {
                if (
                  isContextualCaptureEntitled === false &&
                  oldValue === true
                ) {
                  isContextualCaptureEnabled.value = false;
                }
              },
              { immediate: true }
            )
          );
        }
      }
    });
    const hasCalendar = shallowRef<inject.FeatureState>(false);
    inject.hasCalendar(hasCalendar);
    const _hasCalendarFeature = shallowRef(false);
    controller.auth.hasFeature("calendar").then((v) => {
      syncRefs(v, _hasCalendarFeature);
    });
    const hasContacts = shallowRef<inject.FeatureState>(false);
    inject.hasContacts(hasContacts);
    const _hasContactsFeature = shallowRef(false);
    controller.auth.hasFeature("contacts").then((v) => {
      syncRefs(v, _hasContactsFeature);
    });

    const agents = shallowRef<UseAgents | undefined>(undefined);
    inject.agents(agents);
    const automations = shallowRef<UseAutomations | undefined>(undefined);
    inject.automations(automations);
    const automationHotkeys = shallowRef<UseAutomationHotkeys | undefined>(
      undefined
    );
    inject.automationHotkeys(automationHotkeys);
    const canvases = shallowRef<UseCanvases | undefined>(undefined);
    inject.canvases(canvases);

    // Kick off async composables without blocking setup/render.
    const agentsP = releaser.add(useAgents(controller, baseLogger, profile));
    void agentsP
      .then((a) => {
        agents.value = a.value;
        logger.debug("useAgents() resolved", a.value);
      })
      .catch((error) => {
        logger.error("useAgents() failed", error);
      });

    const canvasesP = releaser.add(useCanvases(controller, baseLogger));
    void canvasesP
      .then((c) => {
        canvases.value = c.value;
        logger.debug("useCanvases() resolved", c.value);
      })
      .catch((error) => {
        logger.error("useCanvases() failed", error);
      });

    void releaser
      .add(
        agentsP.then((a) =>
          useAutomations(controller, baseLogger, a.value.active)
        )
      )
      .then((u) => {
        automations.value = u.value;
      })
      .catch((error) => {
        logger.error("useAutomations() failed", error);
      });

    // Keyboard shortcuts bound to manual automations (per-account,
    // device-local). The listener is created once here — the automation
    // configuration screen needs the mapping API — but it only fires on
    // object pages, whose top bars register the viewed object (the
    // automation's input) while mounted.
    automationHotkeys.value = releaser.add(
      useAutomationHotkeys(
        controller,
        baseLogger,
        profile.value.user.accountId,
        automations
      )
    ).value;

    const palette = createPalette(
      controller,
      baseLogger,
      profile,
      () => paletteModal.value?.open(),
      automations
    );
    releaser.defer(palette.release);
    inject.palette(palette);

    onMounted(() => {
      // Ensure the Canvas type exists in the personal workspace (beta only).
      if (isBetaTester.value) {
        const personalId = profile.value.user.personal.workspaceId;
        void canvasesP
          .then((c) => c.value.initialize(personalId))
          .catch((error) => {
            logger.error("useCanvases().initialize() failed", error);
          });
      }
    });

    // Determine the onboarding method from any pending invitation stored in
    // localStorage by App.vue during the login / deep-link flow.
    const pendingOperation = browser.store.get<{
      type: string;
      id: string;
    } | null>("pending", null, { format: "json" });
    const pendingJoinOrgId = ref<string | null>(
      pendingOperation?.type === "organization" && pendingOperation.id
        ? pendingOperation.id
        : null
    );
    if (pendingOperation) browser.store.unset("pending");

    const onboardingMethod: OnboardingMethod = pendingJoinOrgId.value
      ? { kind: "join", orgId: pendingJoinOrgId.value }
      : { kind: "self" };

    // Listen for pending operations dispatched by App.vue when the user is
    // already logged in (e.g. Electron deep link while the app is open).
    const onPendingOperation = (e: Event) => {
      if (!(e instanceof CustomEvent)) return;
      const detail = e.detail;
      if (detail?.type === "organization" && detail.id) {
        if (hasCompletedOnboarding.value) {
          void runPostSetupQueue(controller, menu, router, logger, [
            { kind: "join_organization", organizationId: detail.id },
          ]);
          return;
        }
        pendingJoinOrgId.value = detail.id;
      }
    };
    document.addEventListener("pending-operation", onPendingOperation);
    releaser.defer(() => {
      document.removeEventListener("pending-operation", onPendingOperation);
    });

    // Drains any post-setup operations queued during sign-up / login: the
    // pending org-join (from an invitation_token), a pending marketplace
    // extension install, and any operations stashed in browser storage by the
    // onboarding wizard. Safe to call when nothing is queued (no-op).
    const drainPostSetupQueue = async () => {
      const extensionId = browser.store.get<string | undefined>(
        "temporary:extensionId",
        undefined
      );
      if (extensionId) {
        browser.store.unset("temporary:extensionId");
      }
      const joinOrganizationId = pendingJoinOrgId.value;
      pendingJoinOrgId.value = null;

      const built = buildPostSetupOperations({
        joinOrganizationId,
        extensionId,
      });

      // Queued operations stay in storage while they run; runPostSetupQueue
      // removes each one once it has been performed or has failed, so
      // operations interrupted by an app quit are retried at the next launch.
      const queuedFromApp = browser.store.get<PostSetupOperation[] | null>(
        POST_ONBOARDING_QUEUED_OPS_KEY,
        null,
        { format: "json" }
      );

      const setupOrgOps =
        queuedFromApp?.filter(
          (
            o
          ): o is Extract<PostSetupOperation, { kind: "setup_organization" }> =>
            o.kind === "setup_organization"
        ) ?? [];
      // Also carries operations of kinds queued by older versions and no
      // longer handled (e.g. "set_default_meeting_automation"): the queue
      // no-ops on them and removes them from storage.
      const restQueued =
        queuedFromApp?.filter((o) => o.kind !== "setup_organization") ?? [];

      const joinOps = built.filter((o) => o.kind === "join_organization");
      const extOps = built.filter((o) => o.kind === "install_extension");

      const operations = [...joinOps, ...setupOrgOps, ...extOps, ...restQueued];

      if (operations.length === 0) return;

      try {
        await runPostSetupQueue(controller, menu, router, logger, operations);
      } catch (e) {
        logger.error("post-setup queue failed", e);
      }
    };

    // For users who skip the onboarding wizard (already-onboarded accounts
    // logging in via an invitation_token or a marketplace extension link),
    // drain the queue once the app has mounted so the welcome modal / install
    // modal still appears.
    onMounted(() => {
      if (hasCompletedOnboarding.value) {
        void drainPostSetupQueue();
      }
    });

    // One-shot repair and migration of the personal pages hierarchy at
    // every launch: child edges get their Object block mirrored in the
    // parent's notes, and legacy native `page.parent` values move to the
    // modern extension-row parenting. Fire-and-forget and idempotent — a
    // converged account produces no writes.
    //
    // TODO: to remove at Routine 3.0
    void repairPagesHierarchy(controller, baseLogger, profile).catch(
      (e: unknown) => {
        logger.error("pages hierarchy repair failed", { error: e });
      }
    );

    // Ensure a meeting automation is selected at every launch: seeds
    // accounts that predate the sign-up seeding and fresh devices (the
    // selection is stored per device), and replaces a selection whose
    // automation was deleted — while respecting an explicit "No automation".
    // Fire-and-forget: it waits internally for the assistant and personal
    // organization to replicate (covering brand-new accounts, whose first
    // session runs this during onboarding).
    void setDefaultMeetingAutomation(controller, logger.base(), profile).catch(
      (e) => {
        logger.error("failed to ensure the default meeting automation", e);
      }
    );

    // Building the search index holds the controller's single thread for
    // over a second on large accounts: request it once the first route has
    // rendered, after the requests of its mounted hooks (hence nextTick), so
    // nothing the first screen waits for queues behind it.
    let hasRequestedSearchIndex = false;
    const onRouteResolved = () => {
      if (hasRequestedSearchIndex) return;
      hasRequestedSearchIndex = true;
      void nextTick(() => controller.buildSearchIndex()).catch(
        (error: unknown) => {
          logger.error("failed to build the search index", { error });
        }
      );
    };

    // Methods
    const onOnboardingCompleted = async () => {
      hasCompletedOnboarding.value = true;
      await controller.flag.set("hasCompletedOnboarding", true);
      await drainPostSetupQueue();
    };
    const toggleMenuExpand = async () => {
      isMenuExpanded.value = !isMenuExpanded.value;
      await controller.flag.set("isMenuExpanded", isMenuExpanded.value);
    };
    const rootEl = ref<HTMLElement>();
    const isEditing = ref(false);
    onMounted(() => {
      rootEl.value?.addEventListener("mousemove", stopEditing);
      rootEl.value?.addEventListener("keydown", startEditing);
    });
    watch(isEditing, (value) => {
      if (value) {
        rootEl.value?.addEventListener("mousemove", stopEditing);
        rootEl.value?.removeEventListener("keydown", startEditing);
      } else {
        rootEl.value?.addEventListener("keydown", startEditing);
        rootEl.value?.removeEventListener("mousemove", stopEditing);
      }
    });
    const startEditing = (e: KeyboardEvent) => {
      if (!isEditing.value && !testShortcuts(ref("escape"), e)) {
        isEditing.value = true;
      }
    };
    const stopEditing = () => {
      if (isEditing.value) {
        isEditing.value = false;
      }
    };
    const handleDoubleClick = () => {
      if (in_electron) {
        const { ipcRenderer: ipc } = require("electron");
        ipc.invoke("dblclick-titlebar");
      }
    };
    const openTask = async (task: Task) => {
      await navigateToObject(router, controller, { kind: "task", id: task.id });
    };
    {
      const { hook, subscription } = useNativeNotification(controller, profile);
      releaser.defer(() => hook.detach(subscription));
    }
    {
      const { hook, subscription } = (() => {
        const hook = controller.auth.registerHandler();
        const subscription = hook.attach(async (arg) => {
          match(arg)
            .with({ type: "reauthenticate" }, () => {
              window.console.warn("reauthenticating");
              // FIXME: probably a bit blunt
              window.location.reload();
            })
            .with({ type: "open_url" }, ({ url }) =>
              openExternalUrl(url, controller, true, router)
            )
            .exhaustive();
        });
        return { hook, subscription };
      })();
      releaser.defer(() => hook.detach(subscription));
    }
    {
      const hook = controller.openExternalUrl();
      const subscription = hook.attach((url) => {
        void openExternalUrl(url, controller, true, router);
      });
      releaser.defer(() => hook.detach(subscription));
    }
    const search = () => searchModal.value?.toggle();

    // Where components with debounced saves register their flush; also
    // drained on beforeunload, so closing the window (tab close, app quit)
    // pushes edits still sitting in a debounce window.
    const { flushPendingChanges } = releaser.add(provideFlushPendingChanges());

    const save = () => {
      flushPendingChanges();
      controller.notifications.notify(
        "Do not worry, your modifications are automatically saved!"
      );
      return true;
    };

    const addItem = () => menuEl.value?.menuPrimaryEl?.showAddMenu();

    const reload = () => {
      window.location.reload();
    };

    watch(
      () => route?.name,
      () => {
        content.value?.scrollTo(0, 0);
      },
      { deep: true }
    );
    const isWideDocument = releaser.add(useWideDocument(profile));
    inject.isWideDocument(isWideDocument);

    const [
      clock,
      personalDatabases,
      workspaceDatabases,
      identities,
      localeConfig,
      routineId,
      settings,
      today,
      useFeedback,
      issues,
      calendars,
    ] = await (() => {
      const clock = releaser.add(controller.now());
      const identities = releaser.add(controller.auth.profiles());
      const routineId = controller.auth.id();
      const personalDatabases = controller.graph.database.listPersonal();
      const workspaceDatabases = releaser.add(controller.graph.database.list());
      const today = releaser.add(controller.today());
      const localeConfig = releaser.add(controller.settings.locale());
      const useFeedback = UseFeedback.setInstance(controller, profile);
      const settings = releaser.add(controller.settings.settings());
      const issues = releaser.add(controller.issues());
      const calendars = releaser.add(useCalendars(controller));
      return Promise.all([
        clock,
        personalDatabases,
        workspaceDatabases,
        identities,
        localeConfig,
        routineId,
        settings,
        today,
        useFeedback,
        issues,
        calendars,
      ]);
    })();
    releaser.defer(
      watchEffect(() => {
        const profiles = identities.value;
        hasCalendar.value = !_hasCalendarFeature.value
          ? false
          : profiles.some(
                (p) =>
                  p.kind.provider === "google" &&
                  (p.kind.calendars_state === "synchronizing" ||
                    p.kind.calendars_state === "synchronized")
              )
            ? "synchronized"
            : "initializing";
        hasContacts.value = !_hasContactsFeature.value
          ? false
          : profiles.some(
                (p) =>
                  p.kind.provider === "google" &&
                  (p.kind.contacts_state === "synchronizing" ||
                    p.kind.contacts_state === "synchronized")
              )
            ? "synchronized"
            : "initializing";
      })
    );

    // Update the issues ref with the retrieved value
    issuesRef.value = issues;

    // A *missing* default calendar is inferred from the absence of data, and
    // absence can be transient: right after a Google account connects, the
    // profile can report calendars_state === "synchronized" moments before
    // the controller's default-calendar assignment reaches our ref. Only
    // trust the absence once it has held for a while. Existing-but-ineligible
    // defaults (disabled, read-only) are read off the record itself and need
    // no settling.
    const MISSING_DEFAULT_SETTLE_MS = 10_000;
    const missingDefault = ref(false);
    let missingDefaultTimer: ReturnType<typeof setTimeout> | undefined;
    releaser.defer(
      watchEffect(() => {
        const missing =
          hasCalendar.value === "synchronized" && !calendars.value?.default;
        if (!missing) {
          clearTimeout(missingDefaultTimer);
          missingDefaultTimer = undefined;
          missingDefault.value = false;
        } else if (!missingDefaultTimer && !missingDefault.value) {
          missingDefaultTimer = setTimeout(() => {
            missingDefault.value = true;
          }, MISSING_DEFAULT_SETTLE_MS);
        }
      })
    );
    releaser.defer(() => clearTimeout(missingDefaultTimer));

    // Google connection issues reported by the controller, plus a default
    // calendar that cannot host new events (missing, disabled, or
    // read-only). The latter only once the Google Calendar integration has
    // synchronized: with no calendar account connected (or one still doing
    // its first sync), having no default calendar is normal.
    const accountIssues = computed<AccountIssue[]>(() => {
      const list: AccountIssue[] = issues.value.map((issue) => ({
        kind: "google",
        issue,
      }));
      if (hasCalendar.value === "synchronized") {
        const calendar = calendars.value?.default;
        const issue = calendar
          ? defaultCalendarIssue(calendar)
          : missingDefault.value
            ? "missing"
            : null;
        if (issue) list.push({ kind: "default-calendar", issue });
      }
      return list;
    });

    const { openFatalModal } = launchFatalModal(
      controller,
      newVersion,
      fatalError,
      version,
      identities.value[0]?.email,
      routineId,
      reload,
      logout
    );

    releaser.defer(
      watch(localeConfig, (conf) => (localeProvided.value = locale(conf)), {
        immediate: true,
      })
    );
    UseSettings.setInstance({ controller, locale: localeConfig, settings });
    syncRefs(clock, clockProvided);
    syncRefs(calendars, calendarsProvided);
    syncRefs(today, todayProvided);
    const closeFeedback = () => {
      if (useFeedback.isOpen.value) useFeedback.close();
    };
    releaser.defer(
      watch(
        personalDatabases,
        (db) => {
          if (db) {
            defaultTypesProvided.value = Object.fromEntries(
              Object.entries(db).filter(([id, _entry]) =>
                defaultTableIds.has(id)
              )
            );
            customTypesProvided.value = Object.fromEntries(
              Object.entries(db).filter(([_, database]) =>
                isUserDefinedDatabase(database)
              )
            );
            typesProvided.value = personalDatabases.value;
          }
        },
        { immediate: true }
      )
    );

    releaser.defer(
      watch(
        workspaceDatabases,
        (wdb) => {
          if (wdb) {
            workspaceDatabasesProvided.value = wdb;
          }
        },
        { immediate: true }
      )
    );
    void releaser
      .add(
        useIntegrationTaskBindingRepair(
          controller,
          baseLogger,
          profile,
          integrations,
          workspaceDatabases
        )
      )
      .catch((error) =>
        logger.error("Failed to start integration task binding repair", error)
      );
    await cleanupInitialStaleDatabasePins(
      (await menuItemsPromise).value,
      workspaceDatabases.value
    );

    const shortcuts = computed<Shortcuts>(() => {
      const injection = { isModalShown, isInputFocused };
      const globalShortcuts = {
        ...assignShortcuts(
          injection,
          closeFeedbackShortcut,
          closeFeedback,
          useFeedback.isOpen
        ),
        ...assignShortcuts(injection, goToMenuItem1, () =>
          navigateToQuickAccessMenuItem(0)
        ),
        ...assignShortcuts(injection, goToMenuItem2, () =>
          navigateToQuickAccessMenuItem(1)
        ),
        ...assignShortcuts(injection, goToMenuItem3, () =>
          navigateToQuickAccessMenuItem(2)
        ),
        ...assignShortcuts(injection, goToMenuItem4, () =>
          navigateToQuickAccessMenuItem(3)
        ),
        ...assignShortcuts(injection, goToMenuItem5, () =>
          navigateToQuickAccessMenuItem(4)
        ),
        ...assignShortcuts(injection, goToMenuItem6, () =>
          navigateToQuickAccessMenuItem(5)
        ),
        ...assignShortcuts(injection, goToMenuItem7, () =>
          navigateToQuickAccessMenuItem(6)
        ),
        ...assignShortcuts(injection, goToMenuItem8, () =>
          navigateToQuickAccessMenuItem(7)
        ),
        ...assignShortcuts(injection, goToMenuItem9, () =>
          navigateToQuickAccessMenuItem(8)
        ),
        ...assignShortcuts(injection, goToPreviousMenuItem, () => {
          void findPreviousQuickAccessMenuItemIndex(route).then((index) => {
            if (index !== -1) navigateToQuickAccessMenuItem(index);
          });
        }),
        ...assignShortcuts(injection, goToNextMenuItem, () => {
          void findNextQuickAccessMenuItemIndex(route).then((index) => {
            if (index !== -1) navigateToQuickAccessMenuItem(index);
          });
        }),
        ...assignShortcuts(injection, toggleSearch, search),
        ...assignShortcuts(injection, saveState, save),
        ...assignShortcuts(injection, newItem, addItem),
        ...assignShortcuts(injection, goToSettings, () => {
          navigateTo(RouteNames.Settings, router);
        }),
        ...assignShortcuts(injection, goToShortcuts, () => {
          goToKBShortcuts(controller);
        }),
        ...assignShortcuts(injection, toggleMenu, () => {
          toggleMenuExpand();
        }),
        ...assignShortcuts(injection, toggleDarkModeShortcut, () => {
          themePreference.value =
            themePreference.value === "system"
              ? theme.value === "dark"
                ? "light"
                : "dark"
              : themePreference.value === "dark"
                ? "light"
                : "dark";
        }),
        ...assignShortcuts(injection, goToPreviousScreen, () => {
          navigateBackward(router, menu);
        }),
        ...assignShortcuts(injection, goToNextScreen, () => {
          navigateForward(router);
        }),
      };
      return isModalShown.value || isInputFocused.value
        ? {}
        : in_electron
          ? globalShortcuts
          : Object.assign(globalShortcuts, {
              ...assignShortcuts(injection, toggleModalConsole, () => {
                toggleConsole(controller, profile, router, console);
              }),
            });
    });

    return {
      personalDatabases,
      useFeedback,
      showIssuesModal,
      closeIssuesModal,
      datePicker,
      recurrencePicker,
      destinationPicker,
      assigneePicker,
      statusPicker,
      onOnboardingCompleted,
      console,
      contextMenu,
      fatalError,
      handleDoubleClick,
      hasCompletedOnboarding,
      identities,
      isEditing,
      isMenuExpanded,
      isNotificationShown,
      menuWidth,
      accountIssues,
      menuEl,
      multipleEventsModal,
      newVersion,
      onboardingMethod,
      onRouteResolved,
      openTask,
      reload,
      repeatModal,
      rootEl,
      routeComponentKey,
      search,
      searchModal,
      paletteModal,
      shortcuts,
      simpleModal,
      startEditing,
      stopEditing,
      toggleMenuExpand,
      version,
    };
  },
});
</script>

<style scoped lang="scss">
@use "sass:color";

#content {
  flex-grow: 1;
  flex-shrink: 1;
  display: flex;
  -ms-overflow-style: none;
  scrollbar-width: none;
  width: calc(100% - var(--menu-width, #{$width_menu_collapsed}));
  height: 100%;
  overflow: scroll;

  @include themed() {
    background: t($bg);
  }

  &.agenda {
    height: 100%;
    overflow-y: auto;
  }

  &.Object :deep(.object.wide) {
    padding-inline-start: 96px;
    padding-inline-end: 96px;
  }
}

#root {
  min-width: 800px;
  padding: 0;
  display: flex;
  min-height: 100%;
  width: 100%;
  position: relative;

  .loader {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
  }

  #update {
    position: fixed;
    bottom: 24px;
    inset-inline-end: 24px;
    z-index: $z-index-notifications;
    margin: 0;
    border-radius: 5px;
    padding: 12px 16px;

    @include themed() {
      background: t($secondary);
      color: rgba(t($bg-alt), 0.8);
    }

    a {
      text-decoration: underline;
      display: inline-block;
      padding-inline-start: 4px;
      cursor: pointer;
    }

    a,
    span {
      @include themed() {
        color: t($bg-alt);
      }
    }
  }

  #titlebar {
    &.isMenuExpanded {
      :deep(.task-header) {
        margin-inline-start: var(--menu-width, #{$width_menu_expanded});
      }
    }
  }

  #onboarding {
    text-align: center;
    margin: 0 auto;
    width: 100%;
    height: 100%;

    .step {
      .description {
        position: absolute;
        bottom: 100px;
        padding: 0;
        margin: 0 auto;
        width: 100%;

        @include themed() {
          background: t($bg);
        }

        h2 {
          @extend %font-h2;
          margin-bottom: 16px;
        }

        p {
          @extend %font-body-regular;
          max-width: 450px;
          margin: 12px auto;

          @include themed() {
            color: color.adjust(t($grey-40), $lightness: -9%);
          }
        }

        a {
          display: inline-block;
          margin-top: 16px;
        }
      }
    }

    .step2,
    .step3,
    .step4 {
      img {
        width: 584px;
        height: 249px;
        box-shadow: 0px 13px 26px rgba(0, 0, 0, 0.08);
        margin-top: 8%;
      }
    }

    .step1 {
      img {
        width: 651px;
        height: 180px;
        margin-top: 8%;
      }
    }

    .step5 {
      img {
        width: 635px;
        height: 331px;
        margin-top: 5%;
      }

      p a {
        outline: none;
        text-decoration: underline;

        @include themed() {
          color: t($secondary);
        }

        &:hover {
          @include themed() {
            color: t($grey-50);
          }
        }
      }
    }

    .progress {
      position: absolute;
      bottom: 50px;
      text-align: center;
      width: 100%;

      span {
        display: inline-block;
        width: 6px;
        height: 6px;
        border-radius: 6px;
        margin: 0 2px;
        transition: background 300ms ease-out;

        @include themed() {
          border: 1px solid t($grey-40);
        }

        &.filled {
          @include themed() {
            background: t($grey-40);
          }
        }
      }
    }
  }

  .journal-icon {
    margin-inline-start: -2px;
  }
}

.expanded-help {
  width: 100%;
}

.help {
  margin: 12px auto;

  &.isShown {
    :deep(.fill) {
      @include themed() {
        fill: t($secondary);
      }
    }

    .stroke {
      @include themed() {
        stroke: t($secondary);
      }
    }
  }

  &:hover {
    border-radius: 50%;
    @include transition(all 100ms ease-out);

    @include themed() {
      box-shadow: t($icon);
    }
  }
}
</style>
